import { cn } from '../lib';
import { FC, PropsWithChildren } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

const typographyVariants = cva('', {
  variants: {
    variant: {
      h1: 'text-4xl leading-[36px] font-medium', // 36px/36px 500
      h2: 'text-2xl leading-[32px] font-bold', // 24px/32px 700
      h3: 'text-lg leading-[28px] font-semibold', // 18px/28px 600
      h4: 'text-lg leading-[24px] font-medium', // 16px/24px 500
      p: 'text-base leading-[16px] font-medium', // 16px/16px 500

      text1: 'text-sm leading-[20px] font-semibold', // 14px/20px 600
      text2: 'text-sm leading-[20px] font-medium', // 14px/20px 500
      text3: 'text-sm leading-[14px] font-medium', // 14px/14px 500
      text4: 'text-xs leading-[16px] font-semibold', // 12px/16px 600
      text5: 'text-xs leading-[16px] font-medium', // 12px/16px 500

      subtitle1: 'text-sm leading-[20px] font-normal', // 14px/20px 400
      subtitle2: 'text-sm leading-[14px] font-normal', // 14px/14px 400
      subtitle3: 'text-xs leading-[16px] font-normal', // 12px/16px 400
      subtitle4: 'text-xs leading-[12px] font-normal', // 12px/12px 400
      subtitle5: 'text-[13px] leading-[16px] font-normal', // 13px/16px 400
    },
    color: {
      default: 'text-foreground',
      primary: 'text-primary',
      grey: 'text-grey-foreground',
      black: 'text-black-foreground',
      destructive: 'text-destructive',
      muted: 'text-muted',
      chart1: 'text-chart-1',
      chart3: 'text-chart-3',
      sidebarRing: 'text-sidebar-ring',
      badgeStroke02: 'text-badge-stroke02',
      badgeStroke03: 'text-badge-stroke03',
      badgeStroke04: 'text-badge-stroke04',
      badgeStroke05: 'text-badge-stroke05',
      cardForeground: 'text-card-foreground',
      mutedForeground: 'text-muted-foreground',
      primaryForeground: 'text-primary-foreground',
    },
    textTransform: {
      default: 'normal-case',
      lowercase: 'lowercase',
      uppercase: 'uppercase',
      capitalize: 'capitalize',
    },
  },
  defaultVariants: { variant: 'p', color: 'default' },
});

interface TypographyProps extends PropsWithChildren, VariantProps<typeof typographyVariants> {
  dir?: 'ltr' | 'rtl';
  type?: 'div' | 'span';
  onClick?: VoidFunction;
  className?: string;
}

export const Typography: FC<TypographyProps> = ({
  color,
  variant,
  onClick,
  children,
  dir = 'ltr',
  type = 'div',
  textTransform,
  className,
}) => {
  if (type === 'span')
    return (
      <span
        dir={dir}
        onClick={onClick}
        className={cn(typographyVariants({ variant, color, textTransform, className }))}
      >
        {children}
      </span>
    );

  return (
    <div dir={dir} onClick={onClick} className={cn(typographyVariants({ variant, color, textTransform, className }))}>
      {children}
    </div>
  );
};
